import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for real-time Family Network WebSocket updates
 * 
 * Features:
 * - Automatic connection with session token authentication
 * - Auto-reconnection with exponential backoff
 * - Event handlers for family network updates
 * - Connection status tracking
 * - Heartbeat/ping-pong for connection health
 * 
 * Event Types:
 * - invitation_received: Someone sent you an invitation
 * - invitation_accepted: Your invitation was accepted
 * - invitation_declined: Your invitation was declined
 * - member_removed: Family member removed
 * - profile_updated: Family member updated their info
 * - permissions_changed: Your permissions changed
 */

const WEBSOCKET_RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000]; // Exponential backoff
const HEARTBEAT_INTERVAL = 30000; // 30 seconds

export const useFamilyWebSocket = ({
  enabled = true,
  onInvitationReceived,
  onInvitationAccepted,
  onInvitationDeclined,
  onMemberRemoved,
  onProfileUpdated,
  onPermissionsChanged,
  onConnectionChange,
  showToasts = true,
} = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [lastEvent, setLastEvent] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const isManualDisconnectRef = useRef(false);

  // Get the WebSocket URL from the backend URL
  const getWebSocketUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // Convert HTTP(S) to WS(S)
    let wsUrl = backendUrl.replace(/^http/, 'ws');
    
    // Get session token from cookie or localStorage
    const sessionToken = getSessionToken();
    
    if (!sessionToken) {
      return null;
    }
    
    return `${wsUrl}/api/ws/family?token=${sessionToken}`;
  }, []);

  // Get session token from cookies or localStorage
  const getSessionToken = () => {
    // Try to get from cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'session_token') {
        return value;
      }
    }
    
    // Try localStorage as fallback
    return localStorage.getItem('session_token');
  };

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data);
      const { type, data, timestamp } = message;
      
      setLastEvent({ type, data, timestamp, receivedAt: new Date().toISOString() });
      
      switch (type) {
        case 'connected':
          console.log('🔌 Family WebSocket connected');
          break;
          
        case 'ping':
          // Respond to server ping with pong
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'pong' }));
          }
          break;
          
        case 'pong':
          // Server responded to our ping
          break;
          
        case 'invitation_received':
          onInvitationReceived?.(data);
          if (showToasts) {
            toast.info('New Family Invitation', {
              description: data.message || `${data.inviter_name} wants to share with you`,
              action: {
                label: 'View',
                onClick: () => window.location.href = '/emergency-binder/family-network'
              }
            });
          }
          break;
          
        case 'invitation_accepted':
          onInvitationAccepted?.(data);
          if (showToasts) {
            toast.success('Invitation Accepted!', {
              description: data.message || `${data.acceptor_name} joined your family network`
            });
          }
          break;
          
        case 'invitation_declined':
          onInvitationDeclined?.(data);
          if (showToasts) {
            toast.info('Invitation Declined', {
              description: data.message || `${data.decliner_name} declined your invitation`
            });
          }
          break;
          
        case 'member_removed':
          onMemberRemoved?.(data);
          if (showToasts) {
            toast.info('Family Network Updated', {
              description: data.message
            });
          }
          break;
          
        case 'profile_updated':
          onProfileUpdated?.(data);
          if (showToasts) {
            toast.info('Profile Updated', {
              description: data.message || `${data.updater_name} updated their ${data.update_type}`
            });
          }
          break;
          
        case 'permissions_changed':
          onPermissionsChanged?.(data);
          if (showToasts) {
            toast.info('Permissions Changed', {
              description: data.message || `${data.changer_name} updated your access`
            });
          }
          break;
          
        default:
          console.log('Unknown WebSocket message type:', type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }, [
    onInvitationReceived,
    onInvitationAccepted,
    onInvitationDeclined,
    onMemberRemoved,
    onProfileUpdated,
    onPermissionsChanged,
    showToasts
  ]);

  // Start heartbeat to keep connection alive
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, HEARTBEAT_INTERVAL);
  }, []);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    const wsUrl = getWebSocketUrl();
    
    if (!wsUrl) {
      console.log('No session token available for WebSocket connection');
      return;
    }
    
    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    try {
      console.log('🔌 Connecting to Family WebSocket...');
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('✅ Family WebSocket connected');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptRef.current = 0;
        onConnectionChange?.(true);
        startHeartbeat();
      };
      
      ws.onmessage = handleMessage;
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionError('Connection error');
      };
      
      ws.onclose = (event) => {
        console.log(`🔌 Family WebSocket closed: code=${event.code}, reason=${event.reason}`);
        setIsConnected(false);
        onConnectionChange?.(false);
        stopHeartbeat();
        
        // Don't reconnect if manually disconnected or auth failed
        if (isManualDisconnectRef.current || event.code === 4001 || event.code === 4002) {
          return;
        }
        
        // Attempt to reconnect with exponential backoff
        const delay = WEBSOCKET_RECONNECT_DELAYS[
          Math.min(reconnectAttemptRef.current, WEBSOCKET_RECONNECT_DELAYS.length - 1)
        ];
        
        console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current + 1})`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptRef.current += 1;
          connect();
        }, delay);
      };
      
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setConnectionError(error.message);
    }
  }, [getWebSocketUrl, handleMessage, onConnectionChange, startHeartbeat, stopHeartbeat]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    isManualDisconnectRef.current = true;
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    stopHeartbeat();
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, [stopHeartbeat]);

  // Reconnect (useful after login/token refresh)
  const reconnect = useCallback(() => {
    isManualDisconnectRef.current = false;
    reconnectAttemptRef.current = 0;
    connect();
  }, [connect]);

  // Effect: Connect on mount, disconnect on unmount
  useEffect(() => {
    if (!enabled) {
      disconnect();
      return;
    }
    
    // Small delay to ensure auth token is available
    const connectTimeout = setTimeout(() => {
      isManualDisconnectRef.current = false;
      connect();
    }, 500);
    
    return () => {
      clearTimeout(connectTimeout);
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Effect: Reconnect when window regains focus (handles tab sleep)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && enabled && !isConnected) {
        console.log('🔄 Tab became visible, checking WebSocket connection...');
        reconnect();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isConnected, reconnect]);

  return {
    isConnected,
    connectionError,
    lastEvent,
    connect: reconnect,
    disconnect,
  };
};

export default useFamilyWebSocket;
